import React, { useContext, type MouseEvent } from 'react'

import { ActionsRefContext } from '../CardActionRefContext'

const CLICK_THRESHOLD = 200

export function useFastClick<
  T extends HTMLAnchorElement | HTMLButtonElement | HTMLInputElement | null,
>(ref: React.MutableRefObject<T>, options?: { disabled?: boolean }) {
  const context = useContext(ActionsRefContext)

  const down = React.useRef<number>(0)
  const up = React.useRef<number>(0)

  const mouseDown = () => {
    down.current = Date.now()
  }
  const mouseUp = (event: MouseEvent<HTMLElement>) => {
    if (!ref.current || options?.disabled) return

    up.current = Date.now()
    if (up.current - down.current < CLICK_THRESHOLD) {
      const isCardAction = [...context.actionRefs].some((element) =>
        element.contains(event.target as Node),
      )
      if (isCardAction) {
        event.stopPropagation()
        event.preventDefault()
        return
      }

      // Create a new click event with the same modifier keys
      const clickEvent = new window.MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
        button: event.button,
        buttons: event.buttons,
        clientX: event.clientX,
        clientY: event.clientY,
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
        metaKey: event.metaKey,
      })

      ref.current.dispatchEvent(clickEvent)
    }
  }

  return {
    mouseDown,
    mouseUp,
  }
}
